import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { AvForm, AvGroup, AvField } from "availity-reactstrap-validation";
import { Col, Label } from "reactstrap";
import { isNullOrUndefined } from "utils/validations";
import { enumsListStatus } from "utils/enums";
import { useTranslation } from "react-i18next";
import { useFieldsRecordContext } from "contextAPI/FieldsRecordContext";
import cx from "classnames";
import Select from "react-select";
import useList from "hooks/useList";
import usePaginatedSelect from "customsHooks/usePaginatedSelect";

const ComboBoxComposed = (props) => {
  const { t } = useTranslation();
  const {
    id,
    label,
    hide,
    required,
    isPreview,
    isFilterForm,
    itemLink,
    handleOnChangeRecord,
    value,
    parentFieldUuid,
    formValues,
  } = props;

  const { getItems } = useList();
  const { recordForm } = useFieldsRecordContext();

  const [selectedOption, setSelectedOption] = useState(null);
  const [hiddenFieldValue, setHiddenFieldValue] = useState("");
  const [isErrorNotSelectedOption, setIsErrorNotSelectedOption] = useState(false);
  const prevParentValueRef = useRef(null);

  const currentFormValues =
    isNullOrUndefined(formValues) === false && typeof formValues === "object"
      ? formValues
      : recordForm;

  const hasParentDependency = isNullOrUndefined(parentFieldUuid) === false;
  let hideLabelComboBox = null;
  let selectPlaceholder = null;
  let nameItemValue = "";
  let selectedParentValue = "";

  /**
   * Normalizes a field value into a string.
   * - Returns an empty string if the value is null or undefined.
   * - If the value is an object, it returns its `uuid` if present.
   * - Otherwise, it converts the value to a string.
   * @param {*} fieldValue - The value to normalize. Can be of any type.
   * @returns {string} A normalized string representation of the value.
   */
  const normalizeValue = (fieldValue) => {
    if (isNullOrUndefined(fieldValue) === true) {
      return "";
    }
    if (typeof fieldValue === "object") {
      if (isNullOrUndefined(fieldValue.uuid) === false) {
        return String(fieldValue.uuid);
      }
      if (isNullOrUndefined(fieldValue.value) === false) {
        return String(fieldValue.value);
      }
      return "";
    }

    return String(fieldValue);
  };

  /**
   * Sets the selected parent value when a parent dependency exists.
   * @param {boolean} hasParentDependency - Indicates whether the field depends on a parent.
   * @param {Object} recordForm - Form data object containing field values.
   * @param {string} parentFieldUuid - Key used to access the parent field value in the form.
   * @param {Function} normalizeValue - Function used to normalize the parent field value.
   */
  if (hasParentDependency === true) {
    selectedParentValue = normalizeValue(currentFormValues?.[parentFieldUuid]);
  }

  /**
   * Updates the 'nameItemValue' based on the provided 'value' object.
   * This function checks if the 'value' object is defined and if its 'name' property is not null.
   * If both conditions are met, it assigns the value of 'value.uuid' to 'nameItemValue'.
   * @param {object} value - The object to extract the 'uuid' property from.
   * @param {string} nameItemValue - The variable to be updated with the 'uuid' value if conditions are met.
   * @returns {void}
   */
  if (currentFormValues && isNullOrUndefined(currentFormValues[id]) === false) {
    nameItemValue = currentFormValues[id];
  } else if (isNullOrUndefined(value) === false) {
    nameItemValue = value;
  }

    /**
   * Normalizes the current value of the name item.
   * @param {*} nameItemValue - The value to be normalized.
   * @returns {*} The normalized value.
   */
  const normalizedCurrentValue = normalizeValue(nameItemValue);

  /**
   * Filter function: in non-filter-form contexts only active items are shown.
   */
  let filterFn = null;
  if (isFilterForm === false) {
    filterFn = (item) => item.status === enumsListStatus.ACTIVE;
  }

  /**
   * Returns the react-select option value for an item.
   * In filter forms, the value is the item name to allow selecting by name.
   * In regular forms, the value is the item UUID to match the expected field value.
   * @param {Object} item - The item object containing its properties.
   * @returns {string} The value to be used for the react-select option.
   */
  const getItemValue = (item) => {
    if (isFilterForm === true) {
      return item.name;
    }
    return item.uuid;
  };

  const {
    options,
    isLoading,
    loadInitial,
    loadInitialDebounced,
    loadMore,
    reset,
    resetSearch,
  } = usePaginatedSelect({
    fetchFn: (page, perPage, search) =>
      getItems(itemLink, page, perPage, search, selectedParentValue, "and"),
    formatFn: (item) => ({
      value: getItemValue(item),
      label: item.name,
      status: item.status,
    }),
    filterFn: filterFn,
  });

  const isParentSelected = selectedParentValue !== "";
  const isDisabledByParent = hasParentDependency === true && isParentSelected === false;
  const isDisabledByNoItems =
    options.length === 0 && isParentSelected === true && isLoading === false;
  const isDisabledField = isDisabledByParent === true || isDisabledByNoItems === true;

/**
   * Emits a value change event only if the normalized value has changed.
   * @param {*} nextValue - The next value to evaluate and potentially emit.
   */
  const emitValueChangeIfNeeded = (nextValue) => {
    if (normalizeValue(nextValue) !== normalizedCurrentValue) {
      handleOnChangeRecord(id, nextValue);
    }
  };

/**
 * Effect hook to load initial options and handle parent dependency changes.
 * @param {string} itemLink - The API endpoint to fetch items for the combo box.
 * @param {boolean} hide - Indicates whether the field is hidden.
 * @param {string} selectedParentValue - The currently selected value of the parent field, if applicable.
 */
  useEffect(() => {
    if (isPreview === false && hide === true) return;
    prevParentValueRef.current = selectedParentValue;
    reset();
    if (hasParentDependency === true && isParentSelected === false) {
      return;
    }
    loadInitial();
  }, [itemLink, hide, selectedParentValue]);

  /**
   * Effect hook to set the selected option based on the external value prop.
   * This runs whenever the 'value' or 'isFilterForm' props change. It checks if the value is a non-null object with a 'name' property, and if so, it sets the selected option in the combo box accordingly. The value used for the option depends on whether it's a filter form or not.
   * @param {object} value - The external value prop that may contain the selected option's details.
   * @param {boolean} isFilterForm - Indicates whether the component is being used in a filter form context, which affects how the option value is determined.
   */
  useEffect(() => {
    if (
      isNullOrUndefined(value) === false &&
      typeof value === "object" &&
      isNullOrUndefined(value.name) === false
    ) {
      let matchValue = value.uuid;
      if (isFilterForm === true) {
        matchValue = value.name;
      }
      setSelectedOption({ value: matchValue, label: value.name });
      setHiddenFieldValue(matchValue ?? "");
    }
  }, [value, isFilterForm]);

  /**
   * Effect hook to clear the selected option when the normalized current value becomes empty.
   * This ensures that if the current value is cleared (e.g., due to a parent change or an external update), the combo box selection is also cleared to reflect the empty state.
   * @param {string} normalizedCurrentValue - The normalized string representation of the current value, used to determine if the selection should be cleared.
   */
  useEffect(() => {
    if (normalizedCurrentValue === "") {
      setSelectedOption(null);
      setHiddenFieldValue("");
    }
  }, [normalizedCurrentValue]);

  /**
   * Effect hook to handle changes in the parent field value and update the combo box selection accordingly.
   * This effect runs when any of the following dependencies change: options, emitValueChangeIfNeeded, hasParentDependency, id, isDisabledByNoItems, isLoading, normalizedCurrentValue, selectedParentValue.
   * It checks if there is a parent dependency and if the parent value has changed. If the parent value has changed and the current value does not exist in the new options, it emits a value change to clear the selection.
   * @param {Array} options - The current list of options available in the combo box.
   * @param {Function} emitValueChangeIfNeeded - A function to emit a value change event if the new value differs from the current value.
   * @param {boolean} hasParentDependency - Indicates whether this field depends on a parent field's value.
   * @param {string} id - The unique identifier for this field, used when emitting value changes.
   * @param {boolean} isDisabledByNoItems - Indicates whether the combo box is disabled due to having no items available.
   * @param {boolean} isLoading - Indicates whether the options are currently being loaded.
   * @param {string} normalizedCurrentValue - The normalized current value of the field, used to check if it exists in the new options.
   * @param {string} selectedParentValue - The currently selected value of the parent field, used to determine if the parent value has changed.
   */
  useEffect(() => {
    if (hasParentDependency === false) return;
    if (isLoading === true) return;
    if (isDisabledByNoItems === true) return;
    if (normalizedCurrentValue === "") return;

    if (prevParentValueRef.current === selectedParentValue) return;

    const currentValueExists = options.some(
      (option) => String(option.value) === normalizedCurrentValue,
    );
    if (currentValueExists === false) {
      emitValueChangeIfNeeded("");
    }
  }, [
    options,
    emitValueChangeIfNeeded,
    hasParentDependency,
    id,
    isDisabledByNoItems,
    isLoading,
    normalizedCurrentValue,
    selectedParentValue,
  ]);

  /**
   * Handler for changes in the combo box selection.
   * @param {Object} option - The selected option from the combo box.
   */
  const handleChangeComboBox = (option) => {
    setSelectedOption(option);
    setIsErrorNotSelectedOption(required === true && isNullOrUndefined(option) === true);
    let newValue = "";
    if (isNullOrUndefined(option) === false) {
      newValue = option.value;
    }
    setHiddenFieldValue(newValue);
    handleOnChangeRecord(id, newValue);
  };

  /**
   * Handler for when the combo box menu is closed.
   * This function resets the search input and reloads the initial
   * options to ensure that the combo box is refreshed with the default option list when the menu is closed.
   */
  const handleMenuClose = () => {
    if (required === true && isNullOrUndefined(selectedOption) === true) {
      setIsErrorNotSelectedOption(true);
    }
    resetSearch();
    if (hasParentDependency === true && isParentSelected === false) {
      return;
    }
    loadInitial();
  };
  
  /**
   * Handler for changes in the combo box input (search field).
   * This function triggers a debounced load of the initial options based on the current input value,
   * allowing for dynamic searching of options as the user types.
   * @param {string} inputValue - The current value of the combo box input field.
   * @param {Object} action - An object containing details about the input change action.
   */
  const handleInputChange = (inputValue, action) => {
    if (action.action === "input-change") {
      loadInitialDebounced(inputValue);
    }
  };

  if (isLoading === true) {
    selectPlaceholder = t("loading");
  } else {
    selectPlaceholder = t("form.designerLabel10.1");
  }

  /**
   * Sets the `loadingComboBox` content based on the `isLoadingItems` flag.
   * @param {boolean} isLoadingItems - A flag indicating whether items are currently being loaded.
   * @returns {string} The localized text to be displayed in the combo box.
   */
  if (isDisabledByParent === true) {
    selectPlaceholder = t("form.composedFieldSelectParent");
  }

  /**
   * Overrides the default option label when no items are available for the selected parent.
   * This has higher priority than the parent-selected message.
   */
  if (isDisabledByNoItems === true) {
    selectPlaceholder = t("form.noItemsToFill");
  }

  /**
   * Conditionally generates a JSX element to hide or show a labeled comboBox.
   * @param {boolean} hide - A boolean flag indicating whether to hide the labeled comboBox.
   * @param {string} t - A function for translating text labels.
   * @returns {JSX.Element|null} - A JSX element representing a labeled comboBox with optional hiding text, or null if 'hide' is false.
   */
  if (hide === true) {
    hideLabelComboBox = <i className="hide-textBox custom-opacity">({t("form.field49")})</i>;
  }

  /**
   * Generates the JSX element for the combo box component.
   * @returns {JSX.Element} The combo box component.
   */
  const selectComponent = (
    <>
      <Select
        id={id}
        name={id}
        placeholder={selectPlaceholder}
        classNamePrefix={cx("", {
          select: isErrorNotSelectedOption,
        })}
        isLoading={isLoading}
        isDisabled={isDisabledField}
        options={options}
        value={selectedOption}
        onChange={handleChangeComboBox}
        onMenuClose={handleMenuClose}
        onMenuScrollToBottom={loadMore}
        onInputChange={handleInputChange}
        filterOption={null}
        isClearable
      />
      <AvField
        type="hidden"
        name={`${id}_validation`}
        value={hiddenFieldValue}
        errorMessage={t("form.field46")}
        required={required}
      />
    </>
  );

  if (isPreview === true) {
    return (
      <AvForm>
        <AvGroup row>
          <Label
            className={cx("", {
              "is-required": required === true,
            })}
            sm={3}
          >
            {label} {hideLabelComboBox}
          </Label>
          <Col md={8}>
            {selectComponent}
          </Col>
        </AvGroup>
      </AvForm>
    );
  } else if (hide === false && isFilterForm === false) {
    return (
      <AvGroup row>
        <Label
          className={cx("", {
            "is-required": required === true,
          })}
          sm={3}
        >
          {label}
        </Label>
        <Col md={8}>
          {selectComponent}
        </Col>
      </AvGroup>
    );
  } else if (isFilterForm === true) {
    return (
      <Col md={12}>
        {selectComponent}
      </Col>
    );
  } else {
    return null;
  }
};

ComboBoxComposed.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hide: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool.isRequired,
  isFilterForm: PropTypes.bool,
  itemLink: PropTypes.string.isRequired,
  handleOnChangeRecord: PropTypes.func.isRequired,
  value: PropTypes.object,
  childFieldUuid: PropTypes.string,
  parentFieldUuid: PropTypes.string,
  formValues: PropTypes.object,
};

export default React.memo(ComboBoxComposed);
